import { Target, TrendingDown, ShieldCheck, Repeat, Check, X } from "lucide-react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";

const blocks = [
  {
    icon: Target,
    title: "Predictable Outcomes",
    copy: "We focus on defined scopes, measurable process improvements, and structured implementation.",
  },
  {
    icon: TrendingDown,
    title: "Consistent, Repeatable Execution",
    copy: "Our work centers on routine, well-defined business functions rather than one-off, unpredictable initiatives.",
  },
  {
    icon: ShieldCheck,
    title: "Regulatory Alignment",
    copy: "Strong governance and compliance practices reduce the likelihood of avoidable penalties or operational disruption.",
  },
  {
    icon: Repeat,
    title: "Sustainable Value Creation",
    copy: "We help clients build systems and frameworks that support long-term reliability instead of short-term hype.",
  },
];

const comparison = [
  { dimension: "Outcome certainty", low: "Defined scopes & measurable deliverables", high: "Dependent on unproven, one-off bets" },
  { dimension: "Predictability", low: "Routine, well-defined business functions", high: "Unproven, high-variance initiatives" },
  { dimension: "Regulatory posture", low: "Strengthens controls & oversight", high: "Often increases exposure" },
  { dimension: "Value horizon", low: "Long-term reliability", high: "Short-term, hype-driven bets" },
];

const LowRisk = () => (
  <div data-testid="low-risk-page">
    <PageHero
      overline="Our Approach"
      title="A Disciplined, Structured Approach"
      subtitle="Yana Capital Group focuses on consulting that is steady, operationally grounded, and built for consistency — aligned with essential business functions that require oversight and repeatable execution."
      image="https://images.unsplash.com/photo-1517502884422-41eaead166d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBib2FyZHJvb218ZW58MHx8fHwxNzgyODU4MjMzfDA&ixlib=rb-4.1.0&q=85"
    />

    {/* Explanation blocks */}
    <section className="bg-white border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="grid gap-px bg-slate-200 sm:grid-cols-2 border border-slate-200">
          {blocks.map((b, i) => (
            <div key={b.title} className="bg-white p-10 sm:p-12">
              <div className="flex items-center gap-4">
                <span className="font-mono text-sm text-gold">0{i + 1}</span>
                <b.icon size={30} strokeWidth={1.4} className="text-navy" />
              </div>
              <h3 className="mt-6 text-xl font-medium text-navy">{b.title}</h3>
              <p className="mt-3 text-base leading-relaxed text-slate-600">{b.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Comparison matrix */}
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">
            Different from High-Risk Approaches
          </p>
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-navy">
            How We Compare
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600">
            Some consulting engagements depend on aggressive, unproven transformation bets with
            unclear ownership. Our work is different — stable business needs with clear
            operational value.
          </p>
        </div>

        <div className="overflow-hidden border border-slate-200 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 bg-navy text-white text-sm font-semibold uppercase tracking-wide">
            <div className="p-5">Dimension</div>
            <div className="p-5 flex items-center gap-2 text-sage"><Check size={16} /> Yana Capital Group</div>
            <div className="p-5 flex items-center gap-2 text-slate-400"><X size={16} /> High-Risk Alternatives</div>
          </div>
          {comparison.map((row, i) => (
            <div
              key={row.dimension}
              className={`grid grid-cols-1 md:grid-cols-3 border-t border-slate-200 ${i % 2 ? "bg-slate-50" : "bg-white"}`}
            >
              <div className="p-5 font-medium text-navy">{row.dimension}</div>
              <div className="p-5 text-slate-700 flex items-start gap-2">
                <Check size={18} className="text-sage mt-0.5 shrink-0" /> {row.low}
              </div>
              <div className="p-5 text-slate-500 flex items-start gap-2">
                <X size={18} className="text-slate-400 mt-0.5 shrink-0" /> {row.high}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <FinalCTA heading="Make Safer, More Strategic Decisions" buttonLabel="Book Your Consultation" />
  </div>
);

export default LowRisk;
